import React, { useState, useCallback, useEffect } from 'react';
import { runTimelapseExport, isExportSupported } from './encode';
import { purgeSession, countFrames, getTodayCST } from './db';
import { resetCaptureState } from './capture';

const overlayStyle = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0, 0, 0, 0.55)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 99999,
};

const modalStyle = {
  background: '#fff',
  padding: '20px 24px',
  borderRadius: 8,
  minWidth: 240,
  textAlign: 'center',
  fontFamily: 'sans-serif',
  boxShadow: '0 4px 24px rgba(0,0,0,0.2)',
};

const buttonStyleBase = {
  background: '#fff',
  border: '1px solid #ccc',
  borderRadius: 6,
  cursor: 'pointer',
  padding: '10px 16px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 14,
  fontWeight: 600,
  color: '#333',
  lineHeight: 1,
  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
  minHeight: 44,
  touchAction: 'manipulation',
};

const clearButtonStyle = {
  ...buttonStyleBase,
  color: '#c0392b',
};

const triggerDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
};

const TimelapseExportButton = ({ guid }) => {
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [error, setError] = useState(null);
  const [frameCount, setFrameCount] = useState(0);

  useEffect(() => {
    if (!guid) return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const count = await countFrames(guid, getTodayCST());
        if (!cancelled) setFrameCount(count);
      } catch {
        // ignore; we'll retry on the next tick
      }
    };
    tick();
    const interval = setInterval(tick, 2000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [guid]);

  const runExport = useCallback(async () => {
    if (busy) return;
    setError(null);

    if (!guid) {
      setError('No user ID yet — try again in a moment.');
      return;
    }

    if (!isExportSupported()) {
      setError('Timelapse export requires iPadOS 16.4 or newer.');
      return;
    }

    setBusy(true);
    setProgress({ current: 0, total: 0 });
    const date = getTodayCST();
    try {
      const blob = await runTimelapseExport({
        guid,
        date,
        onProgress: (current, total) => setProgress({ current, total }),
      });
      triggerDownload(blob, `daily-art-timelapse-${date}.mp4`);
    } catch (err) {
      console.error('[timelapse] export failed', err);
      setError(err && err.message ? err.message : 'Export failed.');
    } finally {
      setBusy(false);
    }
  }, [busy, guid]);

  const handleExport = useCallback(() => runExport(), [runExport]);

  const handleClear = useCallback(async () => {
    if (busy || !guid) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Clear ${frameCount} captured frame${frameCount === 1 ? '' : 's'}?`)) return;
    try {
      await purgeSession(guid, getTodayCST());
      resetCaptureState();
      setFrameCount(0);
    } catch (err) {
      console.error('[timelapse] clear failed', err);
      setError(err && err.message ? err.message : 'Clear failed.');
    }
  }, [busy, guid, frameCount]);

  return (
    <>
      <div style={{ display: 'flex', gap: 6, pointerEvents: 'auto', flexWrap: 'wrap', justifyContent: 'center' }}>
        <button
          type="button"
          onTouchStart={handleExport}
          onClick={handleExport}
          style={buttonStyleBase}
          title="Export a 10-second timelapse video of your drawing"
        >
          Timelapse ({frameCount})
        </button>
        <button
          type="button"
          onTouchStart={handleClear}
          onClick={handleClear}
          style={clearButtonStyle}
          title="Clear all captured timelapse frames"
        >
          Clear
        </button>
      </div>
      {(busy || error) && (
        <div style={overlayStyle} onClick={() => !busy && setError(null)}>
          <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
            {busy && (
              <>
                <div style={{ marginBottom: 8 }}>Encoding timelapse…</div>
                <div style={{ fontSize: 13, color: '#666' }}>
                  {progress.total > 0
                    ? `Frame ${progress.current} / ${progress.total}`
                    : 'Preparing…'}
                </div>
              </>
            )}
            {!busy && error && (
              <>
                <div style={{ marginBottom: 12 }}>{error}</div>
                <button
                  type="button"
                  onClick={() => setError(null)}
                  style={{
                    padding: '6px 16px',
                    border: '1px solid #ccc',
                    background: '#f5f5f5',
                    borderRadius: 4,
                    cursor: 'pointer',
                  }}
                >
                  OK
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default TimelapseExportButton;
